import React              from 'react';
import classNames         from 'classnames';
import * as PropTypes     from 'prop-types';
import { useTranslation } from 'react-i18next';

import * as func      from '../utils/func';
import * as transform from '../utils/transform';

import RangeSliderHandle from './RangeSliderHandle';
import RangeSliderLegend from './RangeSliderLegend';

const RangeSlider = (props) => {
  const {
          currency,
          data,
          disabled,
          fromTo,
          magnet,
          maxValue,
          minValue,
          onChange,
          ruler,
          step,
          value,
        } = props;

  const [state, setState]           = React.useState(value);
  const [isDragging, setIsDragging] = React.useState(false);

  const { t } = useTranslation();

  const trackNode    = React.useRef(null);
  const lastKeyMoved = React.useRef(null);

  const getSlider = () => trackNode.current.getBoundingClientRect();

  const getHandleStyle = (key) => (func.getPercentageFromValue(state[key], minValue, maxValue) || 0) * 100;

  const getTrackStyle = () => {
    const position = {
      min: func.getPercentageFromValue(state['min'], minValue, maxValue),
      max: func.getPercentageFromValue(state['max'], minValue, maxValue),
    };
    const width    = `${(position.max - position.min) * 100}%`;
    const left     = `${position.min * 100}%`;

    return {
      width: width,
      left:  left,
    };
  };

  const handleStart = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    lastKeyMoved.current = key;

    setIsDragging(true);

    if (!isSSR()) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);
    }
  };

  const handleMove = (event) => {
    if (disabled) {
      return null;
    }

    const position = func.getPositionFromEvent(event, getSlider());

    if (!isSSR()) {
      requestAnimationFrame(() => transform.updatePosition(props, state, handleChange, lastKeyMoved.current, position, getSlider()));
    }
  };

  const handleEnd = (event) => {
    event.preventDefault();

    window.removeEventListener('mousemove', handleMove);
    window.removeEventListener('mouseup', handleEnd);
    window.removeEventListener('touchmove', handleMove);
    window.removeEventListener('touchend', handleEnd);

    setIsDragging(false);
  };

  const handleChange = (value) => {
    setState(prevState => ({
      ...prevState,
      ...value,
    }));
  };

  React.useEffect(() => {
    if (!isDragging && !disabled &&
      (state.min !== value.min || state.max !== value.max)
    ) {
      let newValue = { ...state };

      if (step === 0.1) {
        newValue = {
          min: +Number(newValue.min)
            .toFixed(1),
          max: +Number(newValue.max)
            .toFixed(1),
        };
      }

      onChange(newValue);
    }
  }, [state.min, state.max, isDragging]);

  return (
    <div
      className={classNames(
        'rangeSlider',
        { magnet },
      )}
    >
      <div className="track" ref={trackNode}>
        <div className={'current'} style={getTrackStyle()} />
        <RangeSliderHandle
          type={'min'}
          zIndex={lastKeyMoved.current}
          showLabel={false}
          value={state.min}
          onTouchStart={(event) => handleStart(event, 'min')}
          onMouseDown={(event) => handleStart(event, 'min')}
          left={getHandleStyle('min')}
        />
        <RangeSliderHandle
          type={'max'}
          zIndex={lastKeyMoved.current}
          showLabel={false}
          value={state.max}
          onTouchStart={(event) => handleStart(event, 'max')}
          onMouseDown={(event) => handleStart(event, 'max')}
          left={getHandleStyle('max')}
        />
      </div>
      <RangeSliderLegend
        data={data}
        value={state}
        minValue={minValue}
        maxValue={maxValue}
        showLegend={true}
        fromTo={fromTo}
        showRuler={ruler}
        showCurrency={currency}
        currency={t('label_currency')}
        onChange={(num) => {
        }}
      />
    </div>
  );
};

RangeSlider.defaultProps = {
  disable: false,
  single:  false,
  step:    1,
};

RangeSlider.propTypes = {
  disable:  PropTypes.bool,
  single:   PropTypes.bool,
  step:     PropTypes.number,
  data:     PropTypes.array,
  onChange: PropTypes.func,
  value:    PropTypes.object,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
};

export default RangeSlider;